import React from "react";

const CHUNK_RELOAD_KEY = "lg_admin_chunk_reload";

function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || "");
  const message = String(error.message || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, chunkFailed: false };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      chunkFailed: isChunkLoadError(error),
    };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;

    // One automatic reload per tab session to pick up the new deploy hashes.
    try {
      if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1") return;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    } catch (_) {
      // sessionStorage blocked — still attempt a single reload via flag on window
      if (window.__lgAdminChunkReloaded) return;
      window.__lgAdminChunkReloaded = true;
    }

    // Best-effort: drop Cache Storage leftovers from the old cache-first SW.
    if (typeof caches !== "undefined" && caches.keys) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch(() => {})
        .finally(() => {
          window.location.reload();
        });
      return;
    }

    window.location.reload();
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch (_) {
      /* ignore */
    }
    window.location.reload();
  };

  render() {
    const { children } = this.props;
    const { hasError, chunkFailed } = this.state;
    if (hasError) {
      return (
        <div style={{ padding: 24, fontFamily: "sans-serif" }}>
          <p style={{ marginBottom: 12 }}>
            {chunkFailed
              ? "Admin was updated. Reloading to fetch the latest files…"
              : "Loading failed! Please reload."}
          </p>
          <button type="button" onClick={this.handleReload}>
            Reload
          </button>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
